import type { IconButtonProps } from '@mui/material/IconButton';

import { useState, useCallback } from 'react';

import Box from '@mui/material/Box';
import { Avatar } from '@mui/material';
import Stack from '@mui/material/Stack';
import Drawer from '@mui/material/Drawer';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { stringAvatar } from 'src/utils/convertNameToAvatar';

import { CONFIG } from 'src/config-global';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';

import { useMockedUser, useAuthContext } from 'src/auth/hooks';

import { AccountButton } from './account-button';
import { SignOutButton } from './sign-out-button';

// ----------------------------------------------------------------------

export type AccountDrawerProps = IconButtonProps & {
  data?: {
    label: string;
    href: string;
    icon?: React.ReactNode;
    info?: React.ReactNode;
  }[];
};

export function AccountDrawer({ data = [], sx, ...other }: AccountDrawerProps) {
  const { user: userInfo } = useAuthContext();
  const theme = useTheme();

  const router = useRouter();
  const { user } = useMockedUser();

  const [open, setOpen] = useState(false);

  const handleOpenDrawer = useCallback(() => {
    setOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setOpen(false);
  }, []);

  const handleClickItem = useCallback(
    (path: string) => {
      handleCloseDrawer();
      router.push(path);
    },
    [handleCloseDrawer, router]
  );

  const renderAvatar = (
    <Avatar
      sx={{ width: 150, height: 150 }}
      src={`${CONFIG.site.assetURL}/${userInfo?.avatar?.path}`}
    />
  );

  return (
    userInfo && (
      <>
        <AccountButton
          open={open}
          onClick={handleOpenDrawer}
          photoURL={`${CONFIG.site.assetURL}/${userInfo.avatar?.path}`}
          displayName={userInfo.fullName}
          sx={sx}
          {...other}
        />

        <Drawer
          open={open}
          onClose={handleCloseDrawer}
          anchor="right"
          slotProps={{ backdrop: { invisible: true } }}
          PaperProps={{ sx: { width: 320 } }}
        >
          <IconButton
            onClick={handleCloseDrawer}
            sx={{ top: 12, left: 12, zIndex: 9, position: 'absolute' }}
          >
            <Iconify icon="mingcute:close-line" />
          </IconButton>

          <Scrollbar>
            <Stack alignItems="center" sx={{ pt: 8 }}>
              {userInfo.avatar ? (
                renderAvatar
              ) : (
                <Box sx={{ width: 120, height: 120 }}>
                  <Avatar {...stringAvatar(`${userInfo.fullName}`)} />
                </Box>
              )}

              <Typography variant="subtitle1" noWrap sx={{ mt: 2 }}>
                {userInfo?.fullName}
              </Typography>

              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} noWrap>
                {userInfo?.username}
              </Typography>
            </Stack>
            <Stack
              sx={{
                py: 3,
                px: 2.5,
                borderTop: `dashed 1px ${theme.vars.palette.divider}`,
                borderBottom: `dashed 1px ${theme.vars.palette.divider}`,
              }}
            >
              {data.map((option) => {
                const rootLabel = 'Trang tổng quan';
                const pathRole = paths[userInfo.role];

                const rootHref = paths[userInfo.role].root;

                return (
                  <MenuItem
                    key={option.label}
                    onClick={() =>
                      handleClickItem(
                        option.label === 'Home' ? rootHref : `${`/${userInfo.role}/${option.href}`}`
                      )
                    }
                    sx={{
                      py: 1,
                      color: 'text.secondary',
                      '& svg': { width: 24, height: 24 },
                      '&:hover': { color: 'text.primary' },
                    }}
                  >
                    {option.icon}

                    <Box component="span" sx={{ ml: 2 }}>
                      {option.label === 'Home' ? rootLabel : option.label}
                    </Box>

                    {option.info && (
                      <Label color="error" sx={{ ml: 1 }}>
                        {option.info}
                      </Label>
                    )}
                  </MenuItem>
                );
              })}
            </Stack>
          </Scrollbar>

          <Box sx={{ p: 2.5 }}>
            <SignOutButton onClose={handleCloseDrawer} />
          </Box>
        </Drawer>
      </>
    )
  );
}
